import { computed, DestroyRef, Directive, ElementRef, HostListener, inject, input, OnInit, Self } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, NgControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { isNotNull } from '@libs/utils';
import { PerioConfigFormGroup, PerioMeasurementSurface, TargetIndexMovement } from '../../components/perio-config/perio-config.vm';
import { PerioFormControlService } from '../../services/perio-form-control/perio-form-control.service';
import { PerioExamScreenService } from '../../../perio-exam-screen/services/perio-exam/perio-exam-screen.service';

type PerioConfigMeasurementKeysExcludingPocketDepth = keyof Omit<PerioConfigFormGroup, 'pocketDepth'>;
type PerioControlFormSpecialCharacterMap = Record<string, PerioConfigMeasurementKeysExcludingPocketDepth>;
const specialCharacterMappings: PerioControlFormSpecialCharacterMap = {
  b: 'bleedingOnProbing',
  s: 'suppuration',
  r: 'rec',
};

type HorizontalArrowKeys = 'ArrowLeft' | 'ArrowRight';

@Directive({
  selector: '[libPerioActiveToothFormUserInput]',
  standalone: true,
})
export class PerioActiveToothFormUserInputDirective implements OnInit {
  public surface = input.required<PerioMeasurementSurface>();
  public readonly = input.required<boolean>();
  public reversed = input.required<boolean>();
  public fgDisabled = input<boolean>(false);

  private control?: FormControl<number>;
  private parentForm?: FormGroup<PerioConfigFormGroup>;
  private readonly formControlService = inject(PerioFormControlService, { self: false });
  private readonly perioExamScreenService = inject(PerioExamScreenService);
  private isPocketDepth = false;
  private readonly firstMeasurement = computed(() => (this.reversed() ? 'mesial' : 'distal'));
  private readonly isLastMeasurement = computed(() => this.surface() === (this.reversed() ? 'distal' : 'mesial'));

  @HostListener('keydown', ['$event'])
  protected onKeyDown(e: KeyboardEvent): void {
    e.preventDefault();

    if (e.key === 'Backspace') {
      this.formControlService.setMovedWithoutValue();
      this.handlePocketDepthValueDeletion();
      return;
    }

    if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
      this.handleHorizontalArrowKeys(e.key as HorizontalArrowKeys);
    }

    const currentInputMode = this.formControlService.getInputMode();

    if (/\d/.test(e.key)) {
      // Route to appropriate handler based on current field type and input mode
      if (this.isPocketDepth && currentInputMode === 'pocketDepth') {
        this.handlePocketDepthDigits(+e.key);
      } else if (!this.isPocketDepth) {
        // Handle recession field input (either in recession mode or quick 'R' press in PD mode)
        this.handleRecessionDigits(+e.key);
      }
      return;
    }

    if (e.key.toLowerCase() === 'm') {
      this.formControlService.focusMobility();
      return;
    }

    if (e.key.toLowerCase() === 'f') {
      if (!this.fgDisabled()) {
        this.formControlService.focusFurcuation();
      }
      return;
    }

    if (Object.keys(specialCharacterMappings).includes(e.key.toLowerCase())) {
      this.processMappedCharacters(e.key);
    }
  }
  constructor(
    @Self() private readonly ngControl: NgControl,
    private readonly el: ElementRef<HTMLInputElement>,
    private readonly destroyRef: DestroyRef,
  ) {}

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public ngOnInit(): void {
    this.control = this.ngControl.control as FormControl<number>;
    this.parentForm = this.control.parent?.parent as FormGroup<PerioConfigFormGroup>;

    this.isPocketDepth = this.parentForm?.get('pocketDepth')?.get(this.surface()) === this.control;

    this.el.nativeElement.querySelector('input')?.classList.add('text-center');

    const focusOutSubject$ = new Subject<void>();

    this.el.nativeElement.querySelector('input')!.addEventListener('focus', () => {
      const currentMode = this.formControlService.getInputMode();

      if (this.isPocketDepth && currentMode === 'pocketDepth') {
        this.formControlService.setActivePocketDepthCursor(this.surface(), false, this.reversed());
        this.formControlService.setActiveCursor(this.surface());
      } else if (!this.isPocketDepth && currentMode === 'recession') {
        this.formControlService.setActiveCursor(this.surface());
        this.formControlService.setActiveRecessionCursor(this.surface());
      } else {
        this.formControlService.setActivePocketDepthCursor(this.formControlService.getActivePocketDepthCursor(), false, this.reversed());
        this.formControlService.setActiveRecessionCursor(this.surface());
      }

      this.control!.valueChanges.pipe(takeUntil(focusOutSubject$)).subscribe((val) => {
        const mode = this.formControlService.getInputMode();
        if (mode === 'pocketDepth' && this.isPocketDepth) {
          this.formControlService.setActivePocketDepthCursor(this.surface(), isNotNull(val), this.reversed());
        }
        this.formControlService.setActiveCursor(this.surface());
      });
    });

    this.el.nativeElement.querySelector('input')!.addEventListener('blur', () => {
      this.formControlService.setActiveCursor(null);
      this.formControlService.setActivePocketDepthCursor(null);
      if (!this.isPocketDepth) {
        this.formControlService.setActiveRecessionCursor(null);
      }
      focusOutSubject$.next();
    });

    if (this.isPocketDepth) {
      this.initPocketDepthInput();
    } else {
      this.initRecessionInput();
    }
    this.initBleedingInput();
    this.initSuppurationInput();
  }

  private initPocketDepthInput() {
    this.formControlService.getPocketDepthTrigger$(this.surface(), this.destroyRef, this.readonly).subscribe((digit: number | null) => {
      if (!this.parentForm?.disabled) {
        this.el.nativeElement.querySelector('input')?.focus();
      }
      if (digit !== null && !this.parentForm?.disabled) {
        this.control?.setValue(digit);
      }
    });
  }

  private initRecessionInput() {
    this.formControlService.getRecessionTrigger$(this.surface(), this.destroyRef, this.readonly).subscribe(() => {
      this.el.nativeElement.querySelector('input')?.focus();
    });

    // Listen for recession value changes from voice commands
    this.formControlService
      .getRecessionValue$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // Only set value if this is the active surface
        const activeCursor = this.formControlService.getActiveCursor();
        if (activeCursor === this.surface() && !this.parentForm?.disabled) {
          this.control?.setValue(value);
        }
      });
  }

  private initBleedingInput() {
    this.formControlService.getBleedingTrigger$(this.surface(), this.destroyRef, this.readonly).subscribe(() => {
      const controlToToggle = this.parentForm?.get('bleedingOnProbing')?.get(this.surface() as string);
      controlToToggle?.setValue(true);
    });
  }

  private initSuppurationInput() {
    this.formControlService.getSuppurationTrigger$(this.surface(), this.destroyRef, this.readonly).subscribe(() => {
      const controlToToggle = this.parentForm?.get('suppuration')?.get(this.surface() as string);
      controlToToggle?.setValue(true);
    });
  }

  private handlePocketDepthDigits(digit: number): void {
    if (this.parentForm?.disabled) {
      return;
    }
    if (!this.isPocketDepthMatchActiveCursor()) {
      this.formControlService.focusPocketDepth(
        this.surface(),
        this.reversed(),
        this.isPocketDepth ? TargetIndexMovement.Next : TargetIndexMovement.None,
        digit,
      );
      return;
    }

    this.control?.setValue(digit);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private handleRecessionDigits(digit: number): void {
    const currentMode = this.formControlService.getInputMode();
    const isLastColumn = this.isLastMeasurement();
    const isCentralRecession = this.perioExamScreenService.isCentralRecession();

    if (currentMode === 'recession') {
      // Set the value
      this.control?.setValue(digit);

      // In central-only mode, always move to next tooth after entering central value
      if (isCentralRecession) {
        const lastSurface = this.reversed() ? 'distal' : 'mesial';
        if (this.surface() === 'central') {
          this.formControlService.focusPocketDepth(lastSurface, this.reversed(), TargetIndexMovement.Next);
        } else {
          this.formControlService.focusRecession(this.surface(), this.reversed(), TargetIndexMovement.None);
        }
      } else {
        // Full recession mode: move to next recession field or next tooth
        if (isLastColumn) {
          this.formControlService.focusPocketDepth(this.surface(), this.reversed(), TargetIndexMovement.Next);
        } else {
          // Not at last surface, move to next recession surface
          this.formControlService.focusRecession(this.surface(), this.reversed(), TargetIndexMovement.Next);
          const measurementOrder = this.formControlService.getMeasurementOrder(this.reversed());
          const nextIndex = measurementOrder.indexOf(this.surface()) + 1;
          if (nextIndex < measurementOrder.length) {
            this.formControlService.setActiveCursor(measurementOrder[nextIndex]);
          }
        }
      }
    } else {
      // Quick 'R' press in pocket depth mode
      this.control?.setValue(digit);
      // Original behavior: go back to pocket depth
      const surface = this.perioExamScreenService.isCentralRecession()
        ? this.formControlService.getActivePocketDepthCursor()!
        : this.surface();

      this.formControlService.setActiveRecessionCursor(null);
      this.formControlService.removePreservedDigit();
      this.formControlService.focusPocketDepth(surface, this.reversed(), TargetIndexMovement.None, undefined, false, false);
    }
  }

  private isPocketDepthMatchActiveCursor(): boolean {
    return this.formControlService.getActivePocketDepthCursor() === this.surface();
  }

  private handlePocketDepthValueDeletion(): void {
    if (this.isPocketDepth && !this.isLastMeasurement()) {
      const activePocketDepthControl = this.parentForm!.get('pocketDepth')?.get(this.formControlService.getActivePocketDepthCursor()!);
      const isActivePocketDepthFilled = !!activePocketDepthControl?.value;

      if (isActivePocketDepthFilled) {
        activePocketDepthControl.reset();
        return;
      }
    }

    this.control?.reset();
    this.formControlService.focusPocketDepth(this.surface(), this.reversed(), TargetIndexMovement.Prev);
  }

  private handleHorizontalArrowKeys(key: HorizontalArrowKeys) {
    const direction = key === 'ArrowLeft' ? TargetIndexMovement.Prev : TargetIndexMovement.Next;
    const currentMode = this.formControlService.getInputMode();
    const isCentralRecession = this.perioExamScreenService.isCentralRecession();

    this.formControlService.setMovedWithoutValue();

    // In central-only recession mode during recession input, arrow keys move between teeth
    if (!this.isPocketDepth && currentMode === 'recession' && isCentralRecession) {
      // Move to previous/next tooth by forcing a transition beyond the measurement boundaries
      // For next tooth (right arrow): use last surface + Next to go beyond
      // For previous tooth (left arrow): use first surface + Prev to go beyond
      const surfaceForTransition =
        direction === TargetIndexMovement.Next
          ? this.reversed()
            ? 'distal'
            : 'mesial' // Last surface in order
          : this.reversed()
            ? 'mesial'
            : 'distal'; // First surface in order
      this.formControlService.focusPocketDepth(surfaceForTransition, this.reversed(), direction);
    } else if (this.isPocketDepth || !isCentralRecession) {
      this.formControlService.focusPocketDepth(this.surface(), this.reversed(), direction);
    } else {
      this.formControlService.focusRecession(this.surface(), this.reversed(), direction, isCentralRecession);
    }
  }

  private processMappedCharacters(char: string): void {
    const mappedType: PerioConfigMeasurementKeysExcludingPocketDepth = specialCharacterMappings[char];
    if (mappedType === 'rec') {
      this.formControlService.focusRecession(
        this.surface(),
        this.reversed(),
        TargetIndexMovement.None,
        this.perioExamScreenService.isCentralRecession(),
      );
      return;
    }
    const controlToToggle = this.parentForm?.get(mappedType)?.get(this.surface() as string);
    const currentVal = controlToToggle?.getRawValue() as boolean;
    controlToToggle?.setValue(!currentVal);
  }
}
